import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

type OriginVertical = 'top' | 'center' | 'bottom';
type OriginHorizontal = 'left' | 'center' | 'right';

export type PopoverOrigin = {
  vertical: OriginVertical;
  horizontal: OriginHorizontal;
};

export type PopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  children: React.ReactNode;
  className?: string;
};

type Position = { top: number; left: number };

type AnchorRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

function getOffsetFromRect(rect: AnchorRect, origin: PopoverOrigin): Position {
  const verticalOffset =
    origin.vertical === 'top' ? 0 : origin.vertical === 'center' ? rect.height / 2 : rect.height;
  const horizontalOffset =
    origin.horizontal === 'left' ? 0 : origin.horizontal === 'center' ? rect.width / 2 : rect.width;

  return { top: rect.top + verticalOffset, left: rect.left + horizontalOffset };
}

function getTransform(transformOrigin: PopoverOrigin) {
  const translateX =
    transformOrigin.horizontal === 'left'
      ? '0%'
      : transformOrigin.horizontal === 'center'
      ? '-50%'
      : '-100%';

  const translateY =
    transformOrigin.vertical === 'top'
      ? '0%'
      : transformOrigin.vertical === 'center'
      ? '-50%'
      : '-100%';

  return `translate(${translateX}, ${translateY})`;
}

export default function Popover({
  open,
  anchorEl,
  onClose,
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  transformOrigin = { vertical: 'top', horizontal: 'left' },
  children,
  className
}: PopoverProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });

  const transform = useMemo(() => getTransform(transformOrigin), [transformOrigin]);

  const updatePosition = useCallback(() => {
    if (!anchorEl) return;

    const rect = anchorEl.getBoundingClientRect();
    const rectWithSize: AnchorRect = {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      height: rect.height
    };

    const { top, left } = getOffsetFromRect(rectWithSize, anchorOrigin);
    setPosition({ top, left });
  }, [anchorEl, anchorOrigin]);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, onClose, updatePosition]);

  if (!open || !anchorEl) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[90]" role="presentation">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        onMouseDown={onClose}
        aria-label="Close popover"
      />
      <div
        ref={panelRef}
        className={`absolute rounded-lg bg-white shadow-md border border-gray-200 ${
          className || ''
        }`}
        style={{ top: position.top, left: position.left, transform }}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="false"
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
